import { createContext, useReducer } from 'react';
import type { Dispatch, ReactNode } from 'react';

export enum MediaContextActionType {
	Pause = 'PAUSE',
	Reset = 'RESET',
	Start = 'START',
	Stop = 'STOP',
}

type MediaContextAction = {
	mediaId: string;
	type: MediaContextActionType;
};

export enum MediaContextPlaybackStatus {
	PAUSED = 'PAUSED',
	STARTED = 'STARTED',
	STOPPED = 'STOPPED',
}

type State = {
	currentMediaId: string | null;
	mediaPlaybackStatus: MediaContextPlaybackStatus;
};

type Context = {
	dispatch: Dispatch<MediaContextAction> | null;
	state: State;
};

const initialState: State = { currentMediaId: null, mediaPlaybackStatus: MediaContextPlaybackStatus.STOPPED };

function reducer(state: State, action: MediaContextAction): State {
	switch (action.type) {
		case MediaContextActionType.Pause:
			if (state.currentMediaId !== action.mediaId) {
				return state;
			} else {
				return { ...state, currentMediaId: action.mediaId, mediaPlaybackStatus: MediaContextPlaybackStatus.PAUSED };
			}

		case MediaContextActionType.Reset:
			return initialState;

		case MediaContextActionType.Start:
			return { ...state, currentMediaId: action.mediaId, mediaPlaybackStatus: MediaContextPlaybackStatus.STARTED };

		case MediaContextActionType.Stop:
			if (state.currentMediaId !== action.mediaId) {
				return state;
			} else {
				return { ...state, currentMediaId: action.mediaId, mediaPlaybackStatus: MediaContextPlaybackStatus.STOPPED };
			}
	}
}

export const MediaContext = createContext<Context>({ state: initialState, dispatch: null });
MediaContext.displayName = 'MediaContext';

type MediaContextProviderProps = {
	children: ReactNode;
};

export function MediaContextProvider({ children }: MediaContextProviderProps) {
	const [state, dispatch] = useReducer(reducer, initialState);

	return <MediaContext value={{ dispatch, state }}>{children}</MediaContext>;
}
